/**
 * MAX AET AI Academy - AI Vibe Lexikon Professional
 * Lexicon Data v3.0
 * 
 * Comprehensive bilingual AI glossary with MCG integration
 * 80+ terms covering AI fundamentals, prompting, architecture, safety, and business
 * 
 * @author AET AI Academy + ChatGPT Integration
 * @version 3.0.0
 */

import type { LexiconItem, Language, LangBlock, MiniCourseLink, LexikonStatistics } from "../types";

// Re-export types for convenience
export type { LexiconItem, Language, LangBlock, MiniCourseLink };

/**
 * Complete lexicon database - 80+ AI terms
 * Organized by category with full CZ/EN translations
 */
export const lexicon: LexiconItem[] = [
  // =========================================================================
  // AET METHODOLOGY
  // =========================================================================
  {
    id: "ai-vibe-code",
    level: "Beginner",
    category: "AET Methodology",
    mcgLetter: "A",
    pillar: "A",
    relatedIds: ["aet-method", "prompts-library", "prompt-engineering"],
    cs: {
      term: "AI Vibe Code",
      shortDefinition: "Tvůj osobní styl, jak mluvíš s AI.",
      definition:
        "Soubor principů, frází a preferencí, který určuje, jak zadáváš úkoly AI tak, aby výstupy seděly na tvůj styl práce, hodnoty a cíle.",
      tip: "Napiš si vlastní 'AI Vibe Code manifest' a používej ho v systémových promptech.",
    },
    en: {
      term: "AI Vibe Code",
      shortDefinition: "Your personal way of talking to AI.",
      definition:
        "A set of principles, phrases and preferences that define how you brief AI so outputs match your working style, values and goals.",
      tip: "Write your own 'AI Vibe Code manifesto' and reuse it in system prompts.",
    },
    miniCourses: [
      {
        id: "mcg-aivibe-intro",
        title: "AI Vibe Code: navrhni si svůj osobní styl práce s AI",
        language: "cs",
        url: "https://mcg.aetacademy.com/course/ai-vibe-code-intro",
        type: "quickstart",
        durationMinutes: 20,
      },
    ],
  },
  {
    id: "aet-method",
    level: "Intermediate",
    category: "AET Methodology",
    mcgLetter: "A",
    pillar: "A",
    relatedIds: ["ai-vibe-code", "learning-path"],
    cs: {
      term: "AET metoda",
      shortDefinition: "Rámec AQUA-ECO-TERRA pro humanitně-technologickou práci s AI.",
      definition: "Komplexní metodologie AET AI Academy, která spojuje technické AI dovednosti s lidským přístupem a udržitelností.",
    },
    en: {
      term: "AET Method",
      shortDefinition: "AQUA-ECO-TERRA framework for human-tech work with AI.",
      definition: "Comprehensive AET AI Academy methodology combining technical AI skills with human approach and sustainability.",
    },
  },
  {
    id: "mcg-minicourse",
    level: "Intermediate",
    category: "AET Methodology",
    mcgLetter: "M",
    pillar: "A",
    relatedIds: ["learning-path", "ai-literacy"],
    cs: {
      term: "MCG mini kurz",
      shortDefinition: "Krátký online modul, který buduje konkrétní AI dovednost.",
      definition: "Minicourse Generator kurz - kompaktní vzdělávací modul od 15 do 45 minut zaměřený na jednu konkrétní AI kompetenci.",
    },
    en: {
      term: "MCG Mini-course",
      shortDefinition: "Short online module that builds a specific AI skill.",
      definition: "Minicourse Generator course - compact educational module from 15 to 45 minutes focused on one specific AI competency.",
    },
    miniCourses: [
      {
        id: "mcg-ai-literacy-101",
        title: "AI Literacy 101: základní orientace pro 21. století",
        language: "cs",
        url: "https://mcg.aetacademy.com/course/ai-literacy-101",
        type: "quickstart",
        durationMinutes: 30,
      },
    ],
  },
  {
    id: "learning-path",
    level: "Beginner",
    category: "AET Methodology",
    mcgLetter: "L",
    pillar: "A",
    relatedIds: ["mcg-minicourse"],
    cs: {
      term: "Studijní cesta",
      shortDefinition: "Promyšlená posloupnost MCG mini kurzů k určité kompetenci.",
      definition: "Strukturovaná sekvence vzdělávacích modulů vedoucí od základů k pokročilým dovednostem v dané oblasti.",
    },
    en: {
      term: "Learning Path",
      shortDefinition: "Structured sequence of MCG mini-courses towards a competence.",
      definition: "Structured sequence of educational modules leading from basics to advanced skills in a given area.",
    },
  },

  // =========================================================================
  // PROMPTING & MINDSET
  // =========================================================================
  {
    id: "prompt",
    level: "Beginner",
    category: "Prompting",
    mcgLetter: "P",
    pillar: "A",
    relatedIds: ["prompt-engineering", "system-prompt", "user-prompt"],
    cs: {
      term: "Prompt",
      shortDefinition: "Textové zadání pro AI.",
      definition:
        "Jakýkoli text (instrukce, otázka, ukázka), který pošleš modelu, aby na jeho základě něco vygeneroval nebo vyřešil.",
      example: "'Sepsat shrnutí této schůzky v bodech pro vedení firmy.'",
    },
    en: {
      term: "Prompt",
      shortDefinition: "Text instruction for AI.",
      definition:
        "Any text (instruction, question, example) you send to the model so it can generate or solve something based on it.",
      example: '"Write a bullet-point summary of this meeting for the leadership team."',
    },
  },
  {
    id: "prompt-engineering",
    level: "Intermediate",
    category: "Prompting",
    mcgLetter: "P",
    pillar: "A",
    relatedIds: ["prompt", "system-prompt", "chain-of-thought"],
    cs: {
      term: "Prompt Engineering",
      shortDefinition: "Umění efektivního zadávání úkolů AI.",
      definition:
        "Systematický přístup k navrhování promptů tak, aby byly jasné, strukturované a vedly k použitelným výstupům.",
      tip: "Začni strukturou: role → kontext → úkol → formát výstupu → omezení.",
    },
    en: {
      term: "Prompt Engineering",
      shortDefinition: "The art of tasking AI effectively.",
      definition:
        "A systematic approach to designing prompts so they are clear, structured and produce useful outputs.",
      tip: "Use a structure: role → context → task → output format → constraints.",
    },
  },
  {
    id: "system-prompt",
    level: "Intermediate",
    category: "Prompting",
    mcgLetter: "S",
    pillar: "A",
    relatedIds: ["prompt", "persona"],
    cs: {
      term: "Systémový prompt",
      shortDefinition: "Skrytá hlavní instrukce pro AI.",
      definition:
        "Dlouhodobá instrukce na začátku konverzace, která modelu nastaví roli, styl, hranice a priority.",
    },
    en: {
      term: "System Prompt",
      shortDefinition: "Hidden main instruction for AI.",
      definition:
        "A long-lived instruction at the start of a conversation that defines the model's role, style, boundaries and priorities.",
    },
  },
  {
    id: "user-prompt",
    level: "Beginner",
    category: "Prompting",
    mcgLetter: "U",
    pillar: "A",
    relatedIds: ["prompt", "system-prompt"],
    cs: {
      term: "Uživatelský prompt",
      shortDefinition: "To, co opravdu píšeš do chatu.",
      definition:
        "Konkrétní otázky a zadání, které píšeš v průběhu konverzace a kterými model navádíš v rámci zadané role.",
    },
    en: {
      term: "User Prompt",
      shortDefinition: "What you actually type into chat.",
      definition:
        "Concrete questions and requests you type during the conversation to steer the model within its defined role.",
    },
  },
  {
    id: "persona",
    level: "Beginner",
    category: "Prompting",
    mcgLetter: "P",
    pillar: "A",
    relatedIds: ["system-prompt", "ai-vibe-code"],
    cs: {
      term: "Persona",
      shortDefinition: "Role, kterou AI 'hraje'.",
      definition:
        "Popsaná identita a charakter AI (například zkušený trenér, právník, kouč), podle které model ladí své odpovědi.",
    },
    en: {
      term: "Persona",
      shortDefinition: "The role AI is playing.",
      definition:
        "A described identity and character for AI (for example senior trainer, lawyer, coach) that shapes how it responds.",
    },
  },
  {
    id: "chain-of-thought",
    level: "Intermediate",
    category: "Prompting",
    mcgLetter: "C",
    pillar: "A",
    relatedIds: ["prompt-engineering", "few-shot"],
    cs: {
      term: "Myšlenkový řetězec (Chain-of-Thought)",
      shortDefinition: "Když AI vysvětluje postup krok za krokem.",
      definition:
        "Technika promptingu, kdy AI výslovně požádáš, aby nejdřív popsala svůj postup a teprve potom dala finální odpověď.",
      codeExample: "'Nejprve přemýšlej krok za krokem. Až pak napiš finální odpověď.'",
    },
    en: {
      term: "Chain-of-Thought",
      shortDefinition: "When AI explains its reasoning step-by-step.",
      definition:
        "A prompting technique where you explicitly ask the model to reason step-by-step before giving the final answer.",
      codeExample: '"First think step-by-step, then provide the final answer."',
    },
    miniCourses: [
      {
        id: "mcg-cot",
        title: "Chain-of-Thought v praxi: nauč AI opravdu uvažovat",
        language: "cs",
        url: "https://mcg.aetacademy.com/course/chain-of-thought",
        type: "deepdive",
        durationMinutes: 25,
      },
    ],
  },
  {
    id: "few-shot",
    level: "Intermediate",
    category: "Prompting",
    mcgLetter: "F",
    pillar: "A",
    relatedIds: ["zero-shot", "one-shot"],
    cs: {
      term: "Few-shot učení",
      shortDefinition: "Úkol vysvětlený na několika málo příkladech.",
      definition:
        "Styl zadání, kde AI ukážeš pár ukázkových vstupů a výstupů, aby pochopila vzor, podle kterého má pracovat.",
    },
    en: {
      term: "Few-shot Learning",
      shortDefinition: "Task explained with a few examples.",
      definition:
        "A prompting style where you show the model a couple of input–output examples so it can infer the pattern.",
    },
    miniCourses: [
      {
        id: "mcg-fewshot",
        title: "Few-shot prompting: nauč AI pomocí příkladů",
        language: "cs",
        url: "https://mcg.aetacademy.com/course/few-shot",
        type: "workshop",
        durationMinutes: 20,
      },
    ],
  },
  {
    id: "one-shot",
    level: "Intermediate",
    category: "Prompting",
    mcgLetter: "O",
    pillar: "A",
    relatedIds: ["few-shot", "zero-shot"],
    cs: {
      term: "One-shot učení",
      shortDefinition: "Úkol vysvětlený jedním dobrým příkladem.",
    },
    en: {
      term: "One-shot Learning",
      shortDefinition: "Task explained with a single example.",
    },
  },
  {
    id: "zero-shot",
    level: "Intermediate",
    category: "Prompting",
    mcgLetter: "Z",
    pillar: "A",
    relatedIds: ["few-shot"],
    cs: {
      term: "Zero-shot učení",
      shortDefinition: "AI zvládne úkol jen z popisu, bez příkladů.",
    },
    en: {
      term: "Zero-shot Learning",
      shortDefinition: "Model solves a task from instructions only, without examples.",
    },
  },
  {
    id: "prompts-library",
    level: "Intermediate",
    category: "Prompting",
    mcgLetter: "P",
    pillar: "B",
    relatedIds: ["prompt-template", "ai-vibe-code"],
    cs: {
      term: "Knihovna promptů",
      shortDefinition: "Sdílená sbírka ověřených zadání v týmu nebo organizaci.",
    },
    en: {
      term: "Prompts Library",
      shortDefinition: "Shared collection of proven prompts in a team or organisation.",
    },
  },
  {
    id: "prompt-template",
    level: "Beginner",
    category: "Prompting",
    mcgLetter: "P",
    pillar: "B",
    relatedIds: ["prompts-library"],
    cs: {
      term: "Šablona promptu",
      shortDefinition: "Předpřipravená struktura zadání s místy k doplnění.",
    },
    en: {
      term: "Prompt Template",
      shortDefinition: "Pre-built prompt structure with placeholders to fill in.",
    },
  },

  // =========================================================================
  // CORE MODELS & ARCHITECTURE
  // =========================================================================
  {
    id: "llm",
    level: "Beginner",
    category: "Model",
    mcgLetter: "L",
    pillar: "A",
    relatedIds: ["foundation-model", "transformer", "context-window"],
    cs: {
      term: "LLM (Large Language Model)",
      shortDefinition: "Velký jazykový model pro práci s textem.",
      definition:
        "Model natrénovaný na obrovském množství textu, který umí generovat, doplňovat a chápat lidskou řeč.",
    },
    en: {
      term: "LLM (Large Language Model)",
      shortDefinition: "Large language model for working with text.",
      definition:
        "A model trained on vast amounts of text that can generate, complete and understand human language.",
    },
  },
  {
    id: "foundation-model",
    level: "Intermediate",
    category: "Model",
    mcgLetter: "F",
    pillar: "A",
    relatedIds: ["llm"],
    cs: {
      term: "Základní model (Foundation Model)",
      shortDefinition: "Obecný model, na kterém stavíš další aplikace.",
    },
    en: {
      term: "Foundation Model",
      shortDefinition: "General-purpose model you build applications on.",
    },
  },
  {
    id: "generative-ai",
    level: "Beginner",
    category: "Concept",
    mcgLetter: "G",
    pillar: "A",
    relatedIds: ["llm", "text-to-image", "text-to-speech"],
    cs: {
      term: "Generativní AI",
      shortDefinition: "AI, která vytváří nový obsah – text, obraz, zvuk, kód.",
    },
    en: {
      term: "Generative AI",
      shortDefinition: "AI that creates new content – text, images, audio, code.",
    },
  },
  {
    id: "multimodal",
    level: "Intermediate",
    category: "Capability",
    mcgLetter: "M",
    pillar: "A",
    relatedIds: ["text-to-image", "speech-to-text"],
    cs: {
      term: "Multimodální AI",
      shortDefinition: "Model, který pracuje s více typy vstupů najednou.",
    },
    en: {
      term: "Multimodal AI",
      shortDefinition: "Model that works with multiple input types at once.",
    },
  },
  {
    id: "text-to-image",
    level: "Beginner",
    category: "Task",
    mcgLetter: "T",
    pillar: "A",
    relatedIds: ["diffusion-model"],
    cs: {
      term: "Text-to-Image",
      shortDefinition: "Převod textového zadání na obrázek.",
    },
    en: {
      term: "Text-to-Image",
      shortDefinition: "Generating images from text prompts.",
    },
  },
  {
    id: "text-to-speech",
    level: "Beginner",
    category: "Task",
    mcgLetter: "T",
    pillar: "A",
    cs: {
      term: "Text-to-Speech (TTS)",
      shortDefinition: "Čtení textu syntetickým hlasem.",
    },
    en: {
      term: "Text-to-Speech (TTS)",
      shortDefinition: "Reading text with a synthetic voice.",
    },
  },
  {
    id: "speech-to-text",
    level: "Beginner",
    category: "Task",
    mcgLetter: "S",
    pillar: "A",
    relatedIds: ["text-to-speech"],
    cs: {
      term: "Speech-to-Text (STT)",
      shortDefinition: "Přepis mluvené řeči na text.",
    },
    en: {
      term: "Speech-to-Text (STT)",
      shortDefinition: "Transcribing spoken language into text.",
    },
  },
  {
    id: "nlp",
    level: "Intermediate",
    category: "Field",
    mcgLetter: "N",
    pillar: "A",
    relatedIds: ["llm"],
    cs: {
      term: "NLP (Zpracování přirozeného jazyka)",
      shortDefinition: "Oblast, která řeší porozumění a generování jazyka.",
    },
    en: {
      term: "NLP (Natural Language Processing)",
      shortDefinition: "Field focused on understanding and generating human language.",
    },
  },
  {
    id: "token",
    level: "Beginner",
    category: "Concept",
    mcgLetter: "T",
    pillar: "A",
    relatedIds: ["tokenization", "context-window"],
    cs: {
      term: "Token",
      shortDefinition: "Malý úsek textu, ve kterém model počítá délku.",
      definition:
        "Část slova nebo znaků, kterou model interně používá. Platíš za počet tokenů, ne za počet slov.",
    },
    en: {
      term: "Token",
      shortDefinition: "Small unit of text used for length counting.",
      definition:
        "A piece of word or characters used internally by the model. You pay for tokens, not for human-readable words.",
    },
  },
  {
    id: "tokenization",
    level: "Intermediate",
    category: "Process",
    mcgLetter: "T",
    pillar: "A",
    relatedIds: ["token"],
    cs: {
      term: "Tokenizace",
      shortDefinition: "Rozsekání textu na tokeny, kterým model rozumí.",
    },
    en: {
      term: "Tokenization",
      shortDefinition: "Splitting text into tokens that the model understands.",
    },
  },
  {
    id: "context-window",
    level: "Intermediate",
    category: "Technology",
    mcgLetter: "C",
    pillar: "A",
    relatedIds: ["token", "context-compression"],
    cs: {
      term: "Kontextové okno",
      shortDefinition: "Kolik tokenů si model 'drží v hlavě' najednou.",
    },
    en: {
      term: "Context Window",
      shortDefinition: "How many tokens the model can 'keep in mind' at once.",
    },
  },
  {
    id: "embedding",
    level: "Intermediate",
    category: "Architecture",
    mcgLetter: "E",
    pillar: "A",
    relatedIds: ["vector-db", "similarity-search"],
    cs: {
      term: "Embedding",
      shortDefinition: "Číselný vektor, který zachycuje význam textu.",
    },
    en: {
      term: "Embedding",
      shortDefinition: "Numerical vector representing the meaning of text.",
    },
  },
  {
    id: "vector-db",
    level: "Advanced",
    category: "Architecture",
    mcgLetter: "V",
    pillar: "A",
    relatedIds: ["embedding", "rag"],
    cs: {
      term: "Vektorová databáze",
      shortDefinition: "Databáze pro ukládání embeddingů a podobnostní vyhledávání.",
    },
    en: {
      term: "Vector Database",
      shortDefinition: "Database for storing embeddings and similarity search.",
    },
  },
  {
    id: "similarity-search",
    level: "Intermediate",
    category: "Architecture",
    mcgLetter: "S",
    pillar: "A",
    relatedIds: ["embedding", "vector-db"],
    cs: {
      term: "Vyhledávání podle podobnosti",
      shortDefinition: "Najde texty, které jsou významem nejblíž dotazu.",
    },
    en: {
      term: "Similarity Search",
      shortDefinition: "Finds texts that are semantically closest to the query.",
    },
  },
  {
    id: "rag",
    level: "Intermediate",
    category: "Architecture",
    mcgLetter: "R",
    pillar: "A",
    relatedIds: ["vector-db", "retrieval-pipeline", "hallucination"],
    cs: {
      term: "RAG (Retrieval-Augmented Generation)",
      shortDefinition: "Model, který si před odpovědí dočte tvoje vlastní podklady.",
      definition: "Technika, kdy AI nejprve vyhledá relevantní dokumenty z databáze a teprve pak generuje odpověď na jejich základě.",
    },
    en: {
      term: "RAG (Retrieval-Augmented Generation)",
      shortDefinition: "Model that looks up your own data before answering.",
      definition: "Technique where AI first retrieves relevant documents from a database and then generates an answer based on them.",
    },
  },
  {
    id: "chunking",
    level: "Intermediate",
    category: "Architecture",
    mcgLetter: "C",
    pillar: "A",
    relatedIds: ["rag"],
    cs: {
      term: "Chunkování",
      shortDefinition: "Rozsekání dokumentů na menší kusy pro lepší vyhledávání.",
    },
    en: {
      term: "Chunking",
      shortDefinition: "Splitting documents into smaller pieces for better retrieval.",
    },
  },
  {
    id: "retrieval-pipeline",
    level: "Intermediate",
    category: "Architecture",
    mcgLetter: "R",
    pillar: "A",
    relatedIds: ["rag", "reranking"],
    cs: {
      term: "Retrieval pipeline",
      shortDefinition: "Celý řetězec kroků, jak se z dotazu stane dobrá odpověď.",
    },
    en: {
      term: "Retrieval Pipeline",
      shortDefinition: "Full chain of steps that turns a query into a good answer.",
    },
  },
  {
    id: "reranking",
    level: "Intermediate",
    category: "Architecture",
    mcgLetter: "R",
    pillar: "A",
    relatedIds: ["retrieval-pipeline"],
    cs: {
      term: "Reranking",
      shortDefinition: "Přeřazení nalezených dokumentů podle lepší metriky relevance.",
    },
    en: {
      term: "Reranking",
      shortDefinition: "Reordering retrieved documents using a stronger relevance model.",
    },
  },
  {
    id: "context-compression",
    level: "Advanced",
    category: "Architecture",
    mcgLetter: "C",
    pillar: "A",
    relatedIds: ["context-window", "summarization"],
    cs: {
      term: "Komprese kontextu",
      shortDefinition: "Chytré zkracování podkladů, aby se vešly do kontextového okna.",
    },
    en: {
      term: "Context Compression",
      shortDefinition: "Smart shortening of source material to fit into the context window.",
    },
  },

  // =========================================================================
  // TASKS & CONTENT WORK
  // =========================================================================
  {
    id: "summarization",
    level: "Beginner",
    category: "Task",
    mcgLetter: "S",
    pillar: "B",
    relatedIds: ["context-compression"],
    cs: {
      term: "Shrnutí (Summarization)",
      shortDefinition: "Z delšího textu vytvoří přehlednější, kratší verzi.",
    },
    en: {
      term: "Summarization",
      shortDefinition: "Turns longer text into a shorter, clearer version.",
    },
  },
  {
    id: "translation",
    level: "Beginner",
    category: "Task",
    mcgLetter: "T",
    pillar: "B",
    relatedIds: ["localization"],
    cs: {
      term: "Překlad",
      shortDefinition: "Převedení textu z jednoho jazyka do druhého.",
    },
    en: {
      term: "Translation",
      shortDefinition: "Translating text from one language to another.",
    },
  },
  {
    id: "classification",
    level: "Beginner",
    category: "Task",
    mcgLetter: "C",
    pillar: "A",
    relatedIds: ["extraction", "sentiment-analysis"],
    cs: {
      term: "Klasifikace",
      shortDefinition: "Přiřazení textu do kategorií nebo štítků.",
    },
    en: {
      term: "Classification",
      shortDefinition: "Assigning text into categories or labels.",
    },
  },
  {
    id: "extraction",
    level: "Beginner",
    category: "Task",
    mcgLetter: "E",
    pillar: "A",
    relatedIds: ["classification"],
    cs: {
      term: "Extrakce údajů",
      shortDefinition: "Vytažení konkrétních informací z textu (např. jmen, čísel).",
    },
    en: {
      term: "Information Extraction",
      shortDefinition: "Pulling specific information out of text (names, numbers, entities).",
    },
  },
  {
    id: "sentiment-analysis",
    level: "Beginner",
    category: "Task",
    mcgLetter: "S",
    pillar: "B",
    relatedIds: ["classification"],
    cs: {
      term: "Analýza sentimentu",
      shortDefinition: "Zjišťování, zda je text pozitivní, negativní nebo neutrální.",
    },
    en: {
      term: "Sentiment Analysis",
      shortDefinition: "Detecting whether a text is positive, negative or neutral.",
    },
  },
  {
    id: "code-generation",
    level: "Intermediate",
    category: "Task",
    mcgLetter: "C",
    pillar: "A",
    relatedIds: ["code-refactoring", "copilot"],
    cs: {
      term: "Generování kódu",
      shortDefinition: "AI umí psát nebo doplňovat programátorský kód.",
    },
    en: {
      term: "Code Generation",
      shortDefinition: "AI can write or complete programming code.",
    },
  },
  {
    id: "code-refactoring",
    level: "Intermediate",
    category: "Task",
    mcgLetter: "C",
    pillar: "A",
    relatedIds: ["code-generation"],
    cs: {
      term: "Refaktoring kódu",
      shortDefinition: "Přeorganizování kódu pro vyšší kvalitu bez změny chování.",
    },
    en: {
      term: "Code Refactoring",
      shortDefinition: "Restructuring code for higher quality without changing behaviour.",
    },
  },
  {
    id: "data-cleaning",
    level: "Beginner",
    category: "Task",
    mcgLetter: "D",
    pillar: "B",
    relatedIds: ["extraction"],
    cs: {
      term: "Čištění dat",
      shortDefinition: "Odstranění chyb, duplicit a nepořádku v datech.",
    },
    en: {
      term: "Data Cleaning",
      shortDefinition: "Removing errors, duplicates and mess from data.",
    },
  },

  // =========================================================================
  // RISKS, SAFETY, ETHICS
  // =========================================================================
  {
    id: "hallucination",
    level: "Beginner",
    category: "Risk",
    mcgLetter: "H",
    pillar: "A",
    relatedIds: ["rag", "safety"],
    cs: {
      term: "Halucinace modelu",
      shortDefinition: "Sebevědomá odpověď, která ale není pravdivá.",
      definition: "Situace, kdy AI generuje fakticky nepravdivé informace, které znějí věrohodně.",
    },
    en: {
      term: "Model Hallucination",
      shortDefinition: "Confident-sounding answer that is factually wrong.",
      definition: "Situation where AI generates factually incorrect information that sounds plausible.",
    },
  },
  {
    id: "safety",
    level: "Beginner",
    category: "Safety",
    mcgLetter: "S",
    pillar: "A",
    relatedIds: ["guardrails", "alignment"],
    cs: {
      term: "Bezpečnost AI (Safety)",
      shortDefinition: "Jak zajistit, aby AI neškodila lidem ani organizaci.",
    },
    en: {
      term: "AI Safety",
      shortDefinition: "How to ensure AI does not harm people or organisations.",
    },
  },
  {
    id: "guardrails",
    level: "Intermediate",
    category: "Safety",
    mcgLetter: "G",
    pillar: "A",
    relatedIds: ["safety", "jailbreak"],
    cs: {
      term: "Guardrails",
      shortDefinition: "Bezpečnostní mantinely, co AI smí a nesmí dělat.",
    },
    en: {
      term: "Guardrails",
      shortDefinition: "Safety boundaries defining what AI is allowed to do.",
    },
  },
  {
    id: "jailbreak",
    level: "Intermediate",
    category: "Risk",
    mcgLetter: "J",
    pillar: "A",
    relatedIds: ["guardrails"],
    cs: {
      term: "Jailbreak prompt",
      shortDefinition: "Zadání, které se snaží obejít bezpečnostní pravidla.",
    },
    en: {
      term: "Jailbreak Prompt",
      shortDefinition: "Prompt trying to bypass the model's safety rules.",
    },
  },
  {
    id: "alignment",
    level: "Intermediate",
    category: "Safety",
    mcgLetter: "A",
    pillar: "A",
    relatedIds: ["rlhf"],
    cs: {
      term: "Alignment",
      shortDefinition: "Zarovnání chování AI s lidskými hodnotami a záměrem.",
    },
    en: {
      term: "Alignment",
      shortDefinition: "Aligning AI behaviour with human values and intent.",
    },
  },
  {
    id: "rlhf",
    level: "Advanced",
    category: "Training",
    mcgLetter: "R",
    pillar: "A",
    relatedIds: ["alignment"],
    cs: {
      term: "RLHF",
      shortDefinition: "Učení s posilováním podle lidské zpětné vazby.",
      definition: "Reinforcement Learning from Human Feedback - metoda trénování AI modelů na základě hodnocení od lidí.",
    },
    en: {
      term: "RLHF",
      shortDefinition: "Reinforcement Learning from Human Feedback.",
      definition: "Reinforcement Learning from Human Feedback - method of training AI models based on human evaluations.",
    },
  },

  // =========================================================================
  // EVALUATION, OPERATIONS, DATA
  // =========================================================================
  {
    id: "evaluation",
    level: "Intermediate",
    category: "Quality & Evaluation",
    mcgLetter: "E",
    pillar: "A",
    relatedIds: ["benchmark", "a-b-testing"],
    cs: {
      term: "Hodnocení modelu",
      shortDefinition: "Jak měřit kvalitu a spolehlivost AI systému.",
    },
    en: {
      term: "Model Evaluation",
      shortDefinition: "How to measure quality and reliability of an AI system.",
    },
  },
  {
    id: "benchmark",
    level: "Intermediate",
    category: "Quality & Evaluation",
    mcgLetter: "B",
    pillar: "A",
    relatedIds: ["evaluation"],
    cs: {
      term: "Benchmark",
      shortDefinition: "Standardizovaný test pro srovnání modelů.",
    },
    en: {
      term: "Benchmark",
      shortDefinition: "Standardised test to compare models.",
    },
  },
  {
    id: "a-b-testing",
    level: "Intermediate",
    category: "Quality & Evaluation",
    mcgLetter: "A",
    pillar: "B",
    relatedIds: ["evaluation"],
    cs: {
      term: "A/B testování",
      shortDefinition: "Porovnání dvou verzí modelu nebo promptu na reálném provozu.",
    },
    en: {
      term: "A/B Testing",
      shortDefinition: "Comparing two versions of a model or prompt on real traffic.",
    },
  },
  {
    id: "feedback-loop",
    level: "Intermediate",
    category: "Quality & Evaluation",
    mcgLetter: "F",
    pillar: "B",
    relatedIds: ["evaluation", "human-in-the-loop"],
    cs: {
      term: "Zpětnovazební smyčka",
      shortDefinition: "Když se reálná zkušenost vrací zpět do vylepšování systému.",
    },
    en: {
      term: "Feedback Loop",
      shortDefinition: "Real-world experience feeding back into system improvement.",
    },
  },
  {
    id: "monitoring",
    level: "Intermediate",
    category: "Operations",
    mcgLetter: "M",
    pillar: "A",
    relatedIds: ["logging", "observability"],
    cs: {
      term: "Monitoring",
      shortDefinition: "Průběžné sledování metrik a chyb AI systému.",
    },
    en: {
      term: "Monitoring",
      shortDefinition: "Continuous tracking of AI system metrics and errors.",
    },
  },
  {
    id: "logging",
    level: "Beginner",
    category: "Operations",
    mcgLetter: "L",
    pillar: "A",
    relatedIds: ["monitoring"],
    cs: {
      term: "Logování",
      shortDefinition: "Zapisování důležitých událostí a dotazů pro pozdější analýzu.",
    },
    en: {
      term: "Logging",
      shortDefinition: "Recording important events and requests for later analysis.",
    },
  },
  {
    id: "observability",
    level: "Intermediate",
    category: "Operations",
    mcgLetter: "O",
    pillar: "A",
    relatedIds: ["monitoring", "logging"],
    cs: {
      term: "Observabilita",
      shortDefinition: "Schopnost systému ukázat, co se v něm děje uvnitř.",
    },
    en: {
      term: "Observability",
      shortDefinition: "System's ability to show what is going on inside.",
    },
  },
  {
    id: "rate-limiting",
    level: "Intermediate",
    category: "Operations",
    mcgLetter: "R",
    pillar: "A",
    relatedIds: ["cost-optimization"],
    cs: {
      term: "Rate limiting",
      shortDefinition: "Omezení počtu API volání za časový úsek.",
    },
    en: {
      term: "Rate Limiting",
      shortDefinition: "Restricting the number of API calls per time period.",
    },
  },
  {
    id: "cost-optimization",
    level: "Intermediate",
    category: "Operations",
    mcgLetter: "C",
    pillar: "B",
    relatedIds: ["rate-limiting", "latency"],
    cs: {
      term: "Optimalizace nákladů",
      shortDefinition: "Strategie, jak neplýtvat tokeny ani GPU časem.",
    },
    en: {
      term: "Cost Optimisation",
      shortDefinition: "Strategies to avoid wasting tokens or GPU time.",
    },
  },
  {
    id: "latency",
    level: "Beginner",
    category: "Operations",
    mcgLetter: "L",
    pillar: "A",
    relatedIds: ["throughput"],
    cs: {
      term: "Latence",
      shortDefinition: "Jak dlouho trvá, než přijde odpověď od modelu.",
    },
    en: {
      term: "Latency",
      shortDefinition: "How long it takes for the model to respond.",
    },
  },
  {
    id: "throughput",
    level: "Intermediate",
    category: "Operations",
    mcgLetter: "T",
    pillar: "A",
    relatedIds: ["latency"],
    cs: {
      term: "Propustnost",
      shortDefinition: "Kolik požadavků zvládne systém paralelně obsloužit.",
    },
    en: {
      term: "Throughput",
      shortDefinition: "How many requests the system can handle in parallel.",
    },
  },

  // =========================================================================
  // DATA & GOVERNANCE
  // =========================================================================
  {
    id: "data-governance",
    level: "Intermediate",
    category: "Data & Governance",
    mcgLetter: "D",
    pillar: "B",
    relatedIds: ["consent-management", "pii"],
    cs: {
      term: "Správa dat (Data Governance)",
      shortDefinition: "Pravidla, jak ve firmě pracovat s daty bezpečně a eticky.",
    },
    en: {
      term: "Data Governance",
      shortDefinition: "Rules for handling data safely and ethically in an organisation.",
    },
  },
  {
    id: "data-anonymization",
    level: "Intermediate",
    category: "Data & Governance",
    mcgLetter: "D",
    pillar: "B",
    relatedIds: ["pii", "redaction"],
    cs: {
      term: "Anonymizace dat",
      shortDefinition: "Úprava dat tak, aby z nich nešlo poznat konkrétní osoby.",
    },
    en: {
      term: "Data Anonymisation",
      shortDefinition: "Transforming data so individuals cannot be identified.",
    },
  },
  {
    id: "pii",
    level: "Intermediate",
    category: "Data & Governance",
    mcgLetter: "P",
    pillar: "B",
    relatedIds: ["data-anonymization", "redaction"],
    cs: {
      term: "PII (Osobní identifikovatelné údaje)",
      shortDefinition: "Údaje, podle kterých lze konkrétní osobu přímo určit.",
    },
    en: {
      term: "PII (Personally Identifiable Information)",
      shortDefinition: "Data that directly identify an individual person.",
    },
  },
  {
    id: "redaction",
    level: "Intermediate",
    category: "Data & Governance",
    mcgLetter: "R",
    pillar: "B",
    relatedIds: ["pii"],
    cs: {
      term: "Redakce dat",
      shortDefinition: "Zakrytí nebo odstranění citlivých údajů v textu.",
    },
    en: {
      term: "Redaction",
      shortDefinition: "Masking or removing sensitive fields in text or records.",
    },
  },
  {
    id: "consent-management",
    level: "Intermediate",
    category: "Data & Governance",
    mcgLetter: "C",
    pillar: "B",
    relatedIds: ["data-governance"],
    cs: {
      term: "Správa souhlasů",
      shortDefinition: "Evidence toho, s čím uživatelé souhlasili při použití jejich dat.",
    },
    en: {
      term: "Consent Management",
      shortDefinition: "Tracking what users have agreed to regarding their data.",
    },
  },

  // =========================================================================
  // HUMAN, BUSINESS & AET
  // =========================================================================
  {
    id: "human-in-the-loop",
    level: "Beginner",
    category: "Human & Business",
    mcgLetter: "H",
    pillar: "B",
    relatedIds: ["feedback-loop"],
    cs: {
      term: "Human-in-the-loop",
      shortDefinition: "Člověk má stále poslední slovo.",
      definition: "Přístup, kdy člověk kontroluje a schvaluje výstupy AI před jejich použitím.",
    },
    en: {
      term: "Human-in-the-loop",
      shortDefinition: "A human still has the final say.",
      definition: "Approach where a human reviews and approves AI outputs before they are used.",
    },
  },
  {
    id: "copilot",
    level: "Beginner",
    category: "Human & Business",
    mcgLetter: "C",
    pillar: "B",
    relatedIds: ["automation"],
    cs: {
      term: "AI kopilot",
      shortDefinition: "AI jako chytrý pomocník, ne náhrada člověka.",
    },
    en: {
      term: "AI Copilot",
      shortDefinition: "AI as a smart assistant, not a human replacement.",
    },
  },
  {
    id: "automation",
    level: "Beginner",
    category: "Human & Business",
    mcgLetter: "A",
    pillar: "B",
    relatedIds: ["workflow-template"],
    cs: {
      term: "Automatizace",
      shortDefinition: "Převedení opakované rutinní práce na proces řízený AI a skripty.",
    },
    en: {
      term: "Automation",
      shortDefinition: "Turning repeated routine work into a process driven by AI and scripts.",
    },
  },
  {
    id: "change-management",
    level: "Intermediate",
    category: "Human & Business",
    mcgLetter: "C",
    pillar: "M",
    relatedIds: ["ai-literacy"],
    cs: {
      term: "Řízení změny",
      shortDefinition: "Jak připravit lidi na to, že jim v práci přibude AI.",
    },
    en: {
      term: "Change Management",
      shortDefinition: "Preparing people and organisations to work with AI.",
    },
  },
  {
    id: "ai-literacy",
    level: "Beginner",
    category: "Human & Business",
    mcgLetter: "A",
    pillar: "M",
    relatedIds: ["super-user", "mcg-minicourse"],
    cs: {
      term: "AI gramotnost",
      shortDefinition: "Základní orientace v možnostech a limitech AI.",
    },
    en: {
      term: "AI Literacy",
      shortDefinition: "Basic understanding of AI capabilities and limits.",
    },
  },
  {
    id: "super-user",
    level: "Intermediate",
    category: "Human & Business",
    mcgLetter: "S",
    pillar: "M",
    relatedIds: ["ai-literacy"],
    cs: {
      term: "AI super-user",
      shortDefinition: "Šampion, který táhne adopci AI v týmu.",
    },
    en: {
      term: "AI Super-user",
      shortDefinition: "Champion who drives AI adoption in the team.",
    },
  },
  {
    id: "workflow-template",
    level: "Intermediate",
    category: "Human & Business",
    mcgLetter: "W",
    pillar: "B",
    relatedIds: ["automation"],
    cs: {
      term: "Šablona workflow",
      shortDefinition: "Popis kroků, jak spojit lidi, AI a nástroje do jednoho procesu.",
    },
    en: {
      term: "Workflow Template",
      shortDefinition: "Description of steps connecting people, AI and tools into one process.",
    },
  },

  // =========================================================================
  // ADDITIONAL TECHNICAL TERMS
  // =========================================================================
  {
    id: "open-source-ai",
    level: "Beginner",
    category: "Type",
    mcgLetter: "O",
    pillar: "A",
    relatedIds: ["api"],
    cs: {
      term: "Open-source AI",
      shortDefinition: "Modely a nástroje, jejichž kód nebo váhy jsou veřejně dostupné.",
    },
    en: {
      term: "Open-source AI",
      shortDefinition: "Models and tools whose code or weights are publicly available.",
    },
  },
  {
    id: "api",
    level: "Intermediate",
    category: "Technology",
    mcgLetter: "A",
    pillar: "A",
    relatedIds: ["wrapper"],
    cs: {
      term: "API",
      shortDefinition: "Rozhraní, přes které aplikace volá AI modely.",
    },
    en: {
      term: "API",
      shortDefinition: "Interface through which an application calls AI models.",
    },
  },
  {
    id: "gpu",
    level: "Intermediate",
    category: "Hardware",
    mcgLetter: "G",
    pillar: "A",
    relatedIds: ["latency"],
    cs: {
      term: "GPU",
      shortDefinition: "Grafický procesor – motor pro trénink i běh AI modelů.",
    },
    en: {
      term: "GPU",
      shortDefinition: "Graphics Processing Unit – engine for training and running AI models.",
    },
  },
  {
    id: "wrapper",
    level: "Beginner",
    category: "Application",
    mcgLetter: "W",
    pillar: "A",
    relatedIds: ["api"],
    cs: {
      term: "Wrapper",
      shortDefinition: "Aplikace, která je jen tenkou vrstvou nad cizím modelem.",
    },
    en: {
      term: "Wrapper",
      shortDefinition: "Application that is just a thin layer on top of an external model.",
    },
  },
  {
    id: "agent",
    level: "Advanced",
    category: "Technology",
    mcgLetter: "A",
    pillar: "A",
    relatedIds: ["llm", "copilot"],
    cs: {
      term: "AI Agent",
      shortDefinition: "Autonomní AI, která plánuje a vykonává vícekrokové úkoly.",
      definition: "Pokročilý AI systém, který umí samostatně rozhodovat, plánovat kroky a používat nástroje k dosažení cíle.",
    },
    en: {
      term: "AI Agent",
      shortDefinition: "Autonomous AI that plans and executes multi-step tasks.",
      definition: "Advanced AI system that can independently decide, plan steps and use tools to achieve a goal.",
    },
    miniCourses: [
      {
        id: "mcg-agent-intro",
        title: "AI Agenti: první kroky k autonomní AI",
        language: "cs",
        url: "https://mcg.aetacademy.com/course/ai-agents-intro",
        type: "deepdive",
        durationMinutes: 30,
      },
    ],
  },
  {
    id: "fine-tuning",
    level: "Advanced",
    category: "Training",
    mcgLetter: "F",
    pillar: "A",
    relatedIds: ["foundation-model", "llm"],
    cs: {
      term: "Fine-tuning",
      shortDefinition: "Dotrénování základního modelu na vlastních datech.",
      definition: "Proces, kdy vezmete předtrénovaný model a doladíte ho na specifická data nebo úlohu.",
    },
    en: {
      term: "Fine-tuning",
      shortDefinition: "Training a base model further on custom data.",
      definition: "Process of taking a pre-trained model and adjusting it on specific data or task.",
    },
  },
  {
    id: "inference",
    level: "Intermediate",
    category: "Technology",
    mcgLetter: "I",
    pillar: "A",
    relatedIds: ["llm", "gpu"],
    cs: {
      term: "Inference",
      shortDefinition: "Fáze, kdy model generuje odpovědi (oproti trénování).",
    },
    en: {
      term: "Inference",
      shortDefinition: "Phase when the model generates outputs (as opposed to training).",
    },
  },
  {
    id: "temperature",
    level: "Intermediate",
    category: "Technology",
    mcgLetter: "T",
    pillar: "A",
    relatedIds: ["llm", "prompt-engineering"],
    cs: {
      term: "Teplota (Temperature)",
      shortDefinition: "Parametr, který ovlivňuje kreativitu vs. přesnost modelu.",
      definition: "Nízká teplota = konzistentnější a přesnější odpovědi. Vysoká teplota = kreativnější a rozmanitější.",
    },
    en: {
      term: "Temperature",
      shortDefinition: "Parameter that affects creativity vs. precision of the model.",
      definition: "Low temperature = more consistent and precise answers. High temperature = more creative and diverse.",
    },
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get comprehensive statistics about the lexicon
 */
export const getStatistics = (): LexikonStatistics => {
  const stats: LexikonStatistics = {
    totalTerms: lexicon.length,
    byLevel: {
      Beginner: 0,
      Intermediate: 0,
      Advanced: 0
    },
    byPillar: {
      A: 0,
      B: 0,
      H: 0,
      M: 0,
      Z: 0
    },
    byCategory: {},
    byLetter: {},
    withMiniCourses: 0,
    totalMiniCourses: 0,
    featuredCount: 0
  };

  lexicon.forEach(item => {
    // Count by level
    stats.byLevel[item.level]++;
    
    // Count by pillar
    if (item.pillar) {
      stats.byPillar[item.pillar]++;
    }
    
    // Count by category
    const category = item.category;
    stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
    
    // Count by letter
    const letter = (item.mcgLetter || item.id.charAt(0)).toUpperCase();
    stats.byLetter[letter] = (stats.byLetter[letter] || 0) + 1;
    
    // Count minicourses
    if (item.miniCourses && item.miniCourses.length > 0) {
      stats.withMiniCourses++;
      stats.totalMiniCourses += item.miniCourses.length;
    }
  });

  return stats;
};

/**
 * Find item by ID
 */
export const findById = (id: string): LexiconItem | undefined => {
  return lexicon.find(item => item.id === id);
};

/**
 * Get related items
 */
export const getRelatedItems = (item: LexiconItem): LexiconItem[] => {
  if (!item.relatedIds) return [];
  return item.relatedIds
    .map(id => findById(id))
    .filter((i): i is LexiconItem => i !== undefined);
};

/**
 * Search lexicon with fuzzy matching
 */
export const searchLexicon = (query: string, language: Language = "cs"): LexiconItem[] => {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return lexicon;

  return lexicon.filter(item => {
    const langBlock = item[language];
    const tags = item.tags || [];
    
    return (
      langBlock.term.toLowerCase().includes(normalizedQuery) ||
      langBlock.shortDefinition.toLowerCase().includes(normalizedQuery) ||
      (langBlock.definition && langBlock.definition.toLowerCase().includes(normalizedQuery)) ||
      tags.some(tag => tag.toLowerCase().includes(normalizedQuery)) ||
      item.id.toLowerCase().includes(normalizedQuery) ||
      item.category.toLowerCase().includes(normalizedQuery)
    );
  });
};

/**
 * Get items by category
 */
export const getByCategory = (category: string): LexiconItem[] => {
  return lexicon.filter(item => item.category === category);
};

/**
 * Get items by level
 */
export const getByLevel = (level: string): LexiconItem[] => {
  return lexicon.filter(item => item.level === level);
};

/**
 * Get items with minicourses
 */
export const getWithMiniCourses = (): LexiconItem[] => {
  return lexicon.filter(item => item.miniCourses && item.miniCourses.length > 0);
};

export default lexicon;
